package er.prateek.crossly.config

import er.prateek.crossly.ads.AdsConfig
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/**
 * The app's entire remotely-tunable configuration, as one Remote Config JSON parameter
 * (`app_config`).
 *
 * Deliberately a container of named sections rather than a flat blob: a new area of the app
 * becomes a new field here -- `features`, `content`, `pricing` -- without a new Remote Config
 * parameter, without a console migration, and without older installs breaking when they meet
 * a section they've never heard of. Adding a section is a one-line change to this class.
 *
 * Every level has defaults all the way down, so all of these are equivalent and all leave the
 * app fully working: no parameter at all, `{}`, `{"ads": {}}`, or the full blob.
 */
@Serializable
data class AppConfig(
    val ads: AdsConfig = AdsConfig(),
    val gameplay: GameplayConfig = GameplayConfig(),
    val settings: SettingsVisibilityConfig = SettingsVisibilityConfig(),
    val links: LinksConfig = LinksConfig(),
    val notifications: NotificationsConfig = NotificationsConfig(),
    val modes: ModesConfig = ModesConfig(),
    val streak: StreakConfig = StreakConfig(),
    // Future sections go here, each with its own default:
    // val features: FeaturesConfig = FeaturesConfig(),
) {
    companion object {
        /**
         * Used verbatim on platforms with no Remote Config client (JVM desktop, wasmJs) and
         * before the first successful fetch everywhere else, so behavior is identical on
         * every platform until a fetched config says otherwise.
         */
        val DEFAULT = AppConfig()

        private val json = Json {
            // Tolerating unknown keys is what makes a config written by a newer build safe
            // to activate for an older one -- without it, adding a section to the console
            // blob would break every install that hasn't updated yet.
            ignoreUnknownKeys = true
            isLenient = true
            explicitNulls = false
            // Only affects defaultJson(). Writing every field out makes the seeded fallback
            // self-documenting rather than an opaque "{}".
            encodeDefaults = true
        }

        /**
         * Parses an `app_config` payload, falling back to [DEFAULT] for a blank or malformed
         * blob. A typo in the console must never take the app down, and must never be
         * interpreted as "show more ads".
         *
         * Note this is all-or-nothing by design: a blob that fails to parse falls back
         * wholesale rather than salvaging the sections that happened to be valid, because
         * half-applied config is far harder to reason about during an incident than none.
         */
        fun parse(raw: String): AppConfig {
            if (raw.isBlank()) return DEFAULT
            val parsed = runCatching { json.decodeFromString<AppConfig>(raw) }.getOrDefault(DEFAULT)
            return parsed.copy(modes = parsed.modes.sanitized())
        }

        /** The [DEFAULT] blob as JSON -- seeded as the Remote Config in-app default. */
        fun defaultJson(): String = json.encodeToString(DEFAULT)
    }
}
